import React from "react";

import {
  Editor,
  EditorState,
  Modifier,
  RichUtils,
  getDefaultKeyBinding,
  SelectionState,
  CharacterMetadata,
  EditorBlock,
  ContentState,
  ContentBlock,
  genKey,
  convertToRaw,
  convertFromRaw,
} from "draft-js";
import isSoftNewlineEvent from "draft-js/lib/isSoftNewlineEvent";
import {
  getSelectedBlocksList,
  changeDepth,
  getSelectedBlock,
  getAllBlocks,
  getSelectedBlocksMetadata,
  setBlockData,
  removeAllInlineStyles,
} from "draftjs-utils";

import {
  getSelectionRange,
  getSelectionCoords,
  blockMapBuid,
  blockTableTools,
  tableHandler,
} from "./utils";
import { decorator } from "./decorator";
import { stylemap, FONT_SIZES, FONT_COLORS, PRESETS, BLOCKS } from "./stylemap";

import "draft-js/dist/Draft.css";
import "../../../sass/editor.scss";
import "../../../sass/resizable.scss";
import EntityDialog from "./EntityDialog";
import EditorTableDialog from "./EditorTableDialog";
import PageDialog from "./PageDialog";
import RightToolbar from "./RightToolbar";
import EditorGridCell from "./EditorGridCell";
import { Map, OrderedMap, List } from "immutable";
import Context from "../../Context";

import compressor from "./compressor";
import toHTML from "./toHTML";
import fromHTML from "./fromHTML";
import CrumbsProvider from "../Misc/CrumbsProvider";
import ToolbarA from "./ToolbarA";
import ToolbarB from "./ToolbarB";
import TableBlock from "./TableBlock";
import TableCell from "./TableCell";

import tableStructure from "./tableStructure";
import ParagraphDialog from "./ParagraphDialog";
import BsModal from "../Misc/BsModal";

const PORTRAIT_EDITOR_WIDTH = 931;
const LANDSCAPE_EDITOR_WIDTH = 1139;
const GRID_COLUMNS_COUNT = 12;

export default class TemplateEditor extends React.Component {
  static contextType = Context;
  constructor(props) {
    super(props);
    this.state = {
      editorState: this.initContent(),
      editorStateChanged: false,
      pending: false,

      selectedCells: [],
      selectedBlocks: [],
      selectedBlocksTypes: [],
      currentBlock: {},
      currentTable: false,

      highlightedEntityKey: "",
      selectedEntity: null,

      showEntityDialog: false,
      showTableDialog: false,
      showPageDialog: false,
      showParagraphDialog: false,

      editorFocused: false,

      pageSettings: { orientation: "portrait" },
      currentEditorHeight: null,
      editorWidth: PORTRAIT_EDITOR_WIDTH,
      columnWidth: Math.ceil(PORTRAIT_EDITOR_WIDTH / GRID_COLUMNS_COUNT) - 1,
      currentScale: "100",

      preview: false,

      blockTables: {},
      inlineToolbar: {
        show: false,
        position: {
          top: 0,
          left: 0,
        },
      },
    };
    this.editorComponentRef = React.createRef();
    this.editorRef = React.createRef();
  }

  componentDidMount() {
    window.addEventListener("paste", this.handleWindowPaste);
  }
  componentWillUnmount() {
    window.removeEventListener("paste", this.handleWindowPaste);
  }

  initContent = () => {
    if (this.props.content) {
      return EditorState.createWithContent(
        convertFromRaw(JSON.parse(this.props.content)),
        decorator
      );
    }
    if (this.props.html) {
      return EditorState.createWithContent(
        fromHTML(this.props.html || ""),
        decorator
      );
    }

    return EditorState.createEmpty(decorator);
  };

  onChange = (editorState, callbackFn = false) => {
    this.setState({ editorState, editorStateChanged: true }, () => {
      this.highlightBlocks();
      let block = getSelectedBlock(editorState);
      let currentBlock = block
        ? {
            key: block.getKey(),
            type: block.getType(),
            data: block.getData().toJS(),
            length: block.getLength(),
          }
        : null;
      this.setState({ currentBlock });
      this.adjustEditorHeight();
      if (callbackFn) callbackFn();
    });
  };

  /* ------ Focus, Blur & Highlights ------ */

  //Editor gets focus
  onFocus = () => {
    //Update state and highlight blocks
    this.setState({ editorFocused: true });
    this.highlightBlocks();
  };

  //Editor loses focus
  onBlur = () => {
    //Update state if no dialogs arw shown
    if (
      this.state.showEntityDialog ||
      this.state.showTableDialog ||
      this.state.showParagraphDialog ||
      this.showPageDialog
    )
      return;
    this.setState({ editorFocused: false });
  };

  //Remove highlight from all blocks (DOM)
  highlightBlocksBlur = () => {
    document
      .querySelectorAll(`.editor-block`)
      .forEach((elem) => elem.classList.remove("focus"));
  };

  //Hightlight currently selected blocks (DOM) (compare with state)
  highlightBlocks = () => {
    let blockKeys = [];
    let blockTypes = [];
    let blocksMap = getSelectedBlocksList(this.state.editorState);
    if (blocksMap) {
      for (let block of blocksMap.values()) {
        if (block) {
          blockKeys.push(block.getKey());
          blockTypes.push(block.getType());
        }
      }
      if (
        JSON.stringify(this.state.selectedBlocks) === JSON.stringify(blockKeys)
      )
        return;
      this.setState({
        selectedBlocks: blockKeys,
        selectedBlocksTypes: blockTypes,
      });
      this.highlightBlocksBlur();
      for (let key of blockKeys) {
        var container = document.querySelector(
          `.editor-block[data-offset-key="${key}-0-0"]`
        );
        if (container) container.classList.add("focus");
      }
    }
  };

  highlightCells = () => {
    document
      .querySelectorAll(`td.editor-table-cell`)
      .forEach((elem) => elem.classList.remove("selected"));
    this.state.selectedCells.forEach((key) => {
      var container = document.querySelector(`#editor-table-cell-${key}`);
      if (container) container.classList.add("selected");
    });
  };

  applyPageSettings = (settings) => {
    let pageSettings = settings || this.state.pageSettings;
    let editorWidth =
      pageSettings.orientation === "landscape"
        ? LANDSCAPE_EDITOR_WIDTH
        : PORTRAIT_EDITOR_WIDTH;
    let result = {
      pageSettings,

      editorWidth: editorWidth,
      columnWidth: Math.ceil(editorWidth / GRID_COLUMNS_COUNT) - 1,
      currentScale: "75",
    };

    this.setState(result, () => this.adjustEditorHeight());
  };

  //Highight Entity block by key (DOM only) (compare with state)
  highlightEntity = (entityKey) => {
    if (this.state.highlightedEntityKey !== entityKey) {
      for (let elem of document.querySelectorAll(".editor-entity")) {
        elem.classList.remove("dom-selected");
        if (elem.dataset.entity === entityKey)
          elem.classList.add("dom-selected");
      }
      this.setState({ highlightedEntityKey: entityKey });
    }
  };

  /*  ------ Catch changes by toolbars ------ */

  //Toggle inline styles and solve conflicts
  toggleInlineStyle = (setStyles, callbackFn = false, selection = false) => {
    let editorState = this.state.editorState;
    const currentStyle = editorState.getCurrentInlineStyle();
    if (!Array.isArray(setStyles)) setStyles = [setStyles];

    if (selection) {
      editorState = EditorState.forceSelection(editorState, selection);
    }

    const conflictStyles = [
      ["LH15", "LH2", "LH25"],
      ["RIGHT", "LEFT", "CENTER"],
      ["fontTimes", "fontArial", "fontCourier", "fontComic"],
    ];

    let fontSizesConflict = [];
    FONT_SIZES.forEach((size) => {
      fontSizesConflict.push(`fontSize${size}`);
    });
    conflictStyles.push(fontSizesConflict);

    let fontColorsConflict = [];
    FONT_COLORS.forEach((color) => {
      fontColorsConflict.push(`fontColor${color}`);
    });
    conflictStyles.push(fontColorsConflict);

    conflictStyles.forEach((conflict) => {
      let crossedValues = [...new Set(setStyles)].filter((item) =>
        conflict.includes(item)
      );
      if (crossedValues.length) {
        conflict.forEach((conflictStyle) => {
          if (currentStyle.has(conflictStyle)) {
            editorState = RichUtils.toggleInlineStyle(
              editorState,
              conflictStyle
            );
          }
        });
      }
    });
    setStyles.forEach((inlineStyle) => {
      editorState = RichUtils.toggleInlineStyle(editorState, inlineStyle);
    });

    if (editorState !== this.state.editorState)
      this.onChange(editorState, callbackFn);
  };

  //Togggle block types with some indent/outdent hacks
  toggleBlockType = (blockType) => {
    let callbackFn = false;
    const type = this.state.currentBlock.type;
    const isList =
      type === "unordered-list-item" || type === "ordered-list-item";

    if (blockType === "indent") {
      if (isList) {
        this.onChange(changeDepth(this.state.editorState, 1, 4));
      } else {
        this.blockIndent();
      }
      return;
    }
    if (blockType === "outdent") {
      if (isList) {
        this.onChange(changeDepth(this.state.editorState, -1, 4));
      } else {
        this.blockOutdent();
      }
      return;
    }

    if (blockType === "table") {
      if (this.state.selectedCells.length) {
        this.onTableDialogShow();
      } else {
        this.tableBuild(tableStructure.create(2, 2));
      }
      return;
    }

    if (blockType === "paragraph") {
      this.setState({ showParagraphDialog: true });
      return;
    }

    this.onChange(
      RichUtils.toggleBlockType(this.state.editorState, blockType),
      callbackFn
    );
  };

  togglePreset = (preset) => {
    let editorState = this.state.editorState;
    let selection = editorState.getSelection();
    let contentState = editorState.getCurrentContent();
    const wholeBlock = selection.isCollapsed();
    if (wholeBlock) {
      selection = SelectionState.createEmpty(this.state.currentBlock.key);
      selection = selection.merge({
        focusOffset: this.state.currentBlock.length,
      });
    }

    Object.keys(stylemap).forEach((style) => {
      contentState = Modifier.removeInlineStyle(contentState, selection, style);
    });

    preset.styles.forEach((style) => {
      contentState = Modifier.applyInlineStyle(contentState, selection, style);
    });

    contentState = Modifier.mergeBlockData(
      contentState,
      selection,
      preset.blockData
    );

    editorState = EditorState.push(
      editorState,
      contentState,
      "block-clear-preset"
    );

    this.onChange(editorState);
  };

  //Toggles & merges block data by toolbar
  onToggleBlockData = (dataStyle) => {
    let editorState = this.state.editorState;
    let blockData = Object.assign(dataStyle, {});
    const newContentState = Modifier.mergeBlockData(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      blockData
    );
    editorState = EditorState.push(
      editorState,
      newContentState,
      "change-block-data"
    );
    if (editorState !== this.state.editorState) this.onChange(editorState);
  };

  //Calls custom function by toolbar call
  onCallFunction = (id) => {
    switch (id) {
      case "table-select-row":
        this.tableSelectRow();
        break;
      case "table-select-col":
        this.tableSelectCol();
        break;
      case "table-remove-row":
        this.tableRowRemove();
        break;
      case "table-remove-col":
        this.tableColRemove();
        break;
      case "table-remove":
        this.tableRemove();
        break;
      case "table-add-down":
        this.tableRowColAdd("down");
        break;
      case "table-add-up":
        this.tableRowColAdd("up");
        break;
      case "table-add-left":
        this.tableRowColAdd("left");
        break;
      case "table-add-right":
        this.tableRowColAdd("right");
        break;
      default:
        break;
    }
  };

  //Change entire scale of a document (#edior)
  //& adjust container height
  onToggleScale = (scale) => {
    if (scale === "params") {
      this.setState({ showPageDialog: true });
      return;
    }
    this.setState({ currentScale: scale }, () => {
      this.adjustEditorHeight();
    });
  };
  adjustEditorHeight = () => {
    if (this.editorRef.current) {
      let height = this.editorRef.current.offsetHeight;
      height = (height * parseInt(this.state.currentScale)) / 100;
      this.setState({ currentEditorHeight: `${height}px` });
    }
  };

  /* ------ Handling keys And Paste ------ */

  keyGetHotkeys = () => {
    let result = [
      {
        ctrlKey: false,
        shiftKey: false,
        altKey: true,
        keyCode: 80, //Alt+P
        callback: () => this.toggleBlockType("paragraph"),
      },
      {
        ctrlKey: false,
        shiftKey: false,
        altKey: true,
        keyCode: 84, //Alt+T
        callback: () => this.toggleBlockType("table"),
      },
      {
        ctrlKey: true,
        shiftKey: false,
        altKey: false,
        keyCode: 66, //Ctrl+B
        callback: () => this.toggleInlineStyle("BOLD"),
      },
      {
        ctrlKey: true,
        shiftKey: false,
        altKey: false,
        keyCode: 73, //Ctrl+I
        callback: () => this.toggleInlineStyle("ITALIC"),
      },
      {
        ctrlKey: true,
        shiftKey: false,
        altKey: false,
        keyCode: 85, //Ctrl+U
        callback: () => this.toggleInlineStyle("UNDERLINE"),
      },
      {
        ctrlKey: false,
        shiftKey: true,
        altKey: true,
        keyCode: 107, //Shift+Alt+ +
        callback: () => this.tableColResize(1),
      },
      {
        ctrlKey: false,
        shiftKey: true,
        altKey: true,
        keyCode: 109, //Shift+Alt+ -
        callback: () => this.tableColResize(-1),
      },
      {
        ctrlKey: false,
        shiftKey: true,
        altKey: true,
        keyCode: 39, //Shift+Alt+ ->
        callback: () => this.toggleBlockType("indent"),
      },
      {
        ctrlKey: false,
        shiftKey: true,
        altKey: true,
        keyCode: 37, //Shift+Alt+ <-
        callback: () => this.toggleBlockType("outdent"),
      },
    ];

    for (let k = 0; k < 9; k++) {
      result.push({
        ctrlKey: true,
        shiftKey: false,
        altKey: true,
        keyCode: 49 + k, //Ctrl+Alt+...n...
        callback: () => this.togglePreset(PRESETS[k]),
      });
      result.push({
        ctrlKey: false,
        shiftKey: true,
        altKey: true,
        keyCode: 49 + k, //Shift+Alt+...n...
        callback: () => this.toggleBlockType(BLOCKS[k]),
      });
    }

    return result;
  };

  keyBindingFn = (event) => {
    const { editorState, currentBlock } = this.state;
    const selection = editorState.getSelection();
    const selectionAnchorKey = selection.getAnchorKey();
    const selectionFocusKey = selection.getFocusKey();

    const { key, type } = currentBlock;
    const content = editorState.getCurrentContent();

    const hotKeys = this.keyGetHotkeys();
    hotKeys.forEach((key) => {
      if (
        event.shiftKey === key.shiftKey &&
        event.ctrlKey === key.ctrlKey &&
        event.altKey === key.altKey
      ) {
        if (event.keyCode === key.keyCode) {
          key.callback();
        }
      }
    });

    if (!selection.isCollapsed()) {
      if (selectionAnchorKey !== selectionFocusKey) {
        if (
          this.state.selectedBlocksTypes.includes("table") ||
          this.state.selectedBlocksTypes.includes("cell")
        ) {
          //If selection is crossing table block  we need other procedure
          if (event.key === "Delete" || event.key === "Backspace") {
            this.tableDeleteHandle();
            return false;
          }

          return false;
        }
      }
    } else {
      if (type === "cell" || type === "table") {
        if (event.key === "Backspace") {
          if (selection.getAnchorOffset() === 0) {
            return false;
          }
        }
        if (event.key === "Delete") {
          if (selection.getFocusOffset() === currentBlock.length) {
            return false;
          }
        }
      }
    }

    if (event.key === "Tab") {
      if (type === "cell" || type === "table") {
        const blockAfter = content.getBlockAfter(key);
        if (blockAfter) {
          const blockAfterKey = blockAfter.getKey();
          this.blockForceFocus(blockAfterKey);
        } else {
          this.blockInsertNew({
            type: "unstyled",
            text: "",
            characterList: List(),
          });
        }
        return false;
      }

      if (type === "grid") {
        const blockAfter = content.getBlockAfter(key);
        if (blockAfter) {
          const blockAfterType = blockAfter.getType();
          const blockAfterKey = blockAfter.getKey();

          if (blockAfterType === "grid") {
            this.blockForceFocus(blockAfterKey);
            return false;
          } else {
            this.blockInsertNew(
              {
                type: "grid",
                text: "",
                characterList: List(),
              },
              blockAfterKey
            );
          }
        } else {
          this.blockInsertNew({
            type: "grid",
            text: "",
            characterList: List(),
          });
        }
      } else {
        this.onChange(RichUtils.onTab(event, editorState, 3));
      }
      return false;
    }

    return getDefaultKeyBinding(event);
  };

  handleReturn = (event) => {
    const { editorState, currentBlock } = this.state;
    if (isSoftNewlineEvent(event)) {
      this.onChange(RichUtils.insertSoftNewline(editorState));
      return "handled";
    }
    const { type } = currentBlock;
    if (type === "grid" || type === "table" || type === "cell") {
      this.onChange(RichUtils.insertSoftNewline(editorState));
      return "handled";
    }

    return "not-handled";
  };

  handleWindowPaste = (e) => {
    if (e.clipboardData) {
      var items = e.clipboardData.items;
      if (items) {
        for (var i = 0; i < items.length; i++) {
          if (items[i].type.indexOf("image") !== -1) {
            this.handleDroppedImage(items[i].getAsFile());
          }
        }
      }
    }
  };

  handlePastedText = (text, html, editorState) => {
    if (this.editorComponentRef.current) {
      const fragment = this.editorComponentRef.current.getClipboard();

      if (fragment) {
        let entitiesInFragment = this.entitiesInFragment(fragment);

        let finalFragment = null;

        if (entitiesInFragment.length > 0) {
          if (entitiesInFragment.length === 1 && text.trim() === "") {
            this.entityPaste(entitiesInFragment[0]);
            return true;
          } else {
            const fragmentUnstyled = this.blockClearTypesInFragment(fragment);
            finalFragment = this.entityClearFromFragment(fragmentUnstyled);
          }
        } else {
          finalFragment = this.blockClearTypesInFragment(fragment);
        }

        let newContent = Modifier.replaceWithFragment(
          editorState.getCurrentContent(),
          editorState.getSelection(),
          finalFragment
        );
        this.onChange(
          EditorState.push(editorState, newContent, "insert-fragment"),
          () => {
            let currBlockData = this.blockDataCollect();
            this.blockDataRestore(currBlockData);
          }
        );

        this.editorComponentRef.current.setClipboard(null);
        return true;
      } else {
        if (text) {
          const finalFragment = ContentState.createFromText(text).blockMap;
          let newContent = Modifier.replaceWithFragment(
            editorState.getCurrentContent(),
            editorState.getSelection(),
            finalFragment
          );
          this.onChange(
            EditorState.push(editorState, newContent, "insert-fragment"),
            () => {
              let currBlockData = this.blockDataCollect();
              this.blockDataRestore(currBlockData);
            }
          );
          return true;
        }
      }
    }
  };

  handleDroppedFiles = (selection, files) => {
    let file = files[0];
    if (file.type.startsWith("image/")) {
      this.handleDroppedImage(file);
    }
    return "handled";
  };

  handleDroppedImage = (file) => {
    if (file) {
      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        this.setState({ pending: false });
      };
      reader.onloadstart = () => {
        this.setState({ pending: true });
      };
      reader.onload = () => {
        compressor(reader.result).then((v) => {
          this.entityInsert("USER_IMAGE", "SEGMENTED", {
            image: v,
            file,
            kind: "image",
            float: "none",
          });
        });
      };
      reader.onerror = () => {
        this.context.toast(reader.error, "error");
      };
    }
  };

  insertFragment = (editorState, fragment) => {
    let newContent = Modifier.replaceWithFragment(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      fragment
    );
    return EditorState.push(editorState, newContent, "insert-fragment");
  };

  /* ------ Entities ------ */

  entityInsertInline = (...entityParams) => {
    let { editorState } = this.state;
    let selection = editorState.getSelection();
    let content = editorState.getCurrentContent();
    if (selection.isCollapsed()) {
      this.context.toast(
        "Выберите фрагмент текста, чтобы вставить ссылку",
        "error"
      );
    } else {
      content = content.createEntity(...entityParams);
      content = Modifier.applyEntity(
        content,
        selection,
        content.getLastCreatedEntityKey()
      );
      this.onChange(
        EditorState.set(editorState, { currentContent: content, selection })
      );
    }
  };

  //Inserts or replaces entity on current selection
  entityInsert = (...entityParams) => {
    let { editorState } = this.state;
    let selection = editorState.getSelection();
    let content = editorState.getCurrentContent();

    content = content.createEntity(...entityParams);
    let modifierFn = selection.isCollapsed()
      ? Modifier.insertText
      : Modifier.replaceText;

    content = modifierFn(
      content,
      selection,
      " ",
      null,
      content.getLastCreatedEntityKey()
    );
    selection = selection.merge({
      anchorOffset: selection.anchorOffset + 1,
      focusOffset: selection.anchorOffset + 1,
    });
    content = Modifier.insertText(content, selection, " ");

    this.onChange(
      EditorState.set(editorState, { currentContent: content, selection })
    );
  };

  //Make copy of entity data, then insert new entity in current
  //selection with copied data, but no name and code
  entityPaste = (entityKey) => {
    const content = this.state.editorState.getCurrentContent();
    const entity = content.getEntity(entityKey);
    const entityData = entity.getData();
    const entityType = entity.getType();
    let entityDataRaw = {};
    for (let dataKey in entityData) {
      let value = entityData[dataKey];
      if (dataKey === "list" && Array.isArray(entityData.list)) {
        value = [];
        entityData.list.forEach((item) => {
          value.push(Object.assign({}, item));
        });
      }
      entityDataRaw[dataKey] = value;
    }
    entityDataRaw.name = "";
    entityDataRaw.code = this.context.key();

    this.entityInsert(entityType, "SEGMENTED", entityDataRaw);
  };

  //Clears all entities from provided fragment
  entityClearFromFragment = (fragment) => {
    let newFragment = blockMapBuid.createFromArray([]);
    fragment.forEach((block, blockKey) => {
      let updatedBlock = block;
      block.findEntityRanges(
        (character) => character.getEntity() !== null,
        (start, end) => {
          updatedBlock = this.entityApplyToContentBlock(
            updatedBlock,
            start,
            end,
            null
          );
          newFragment = newFragment.set(blockKey, updatedBlock);
        }
      );
    });

    return newFragment;
  };

  //Service needs...
  entityApplyToContentBlock = (contentBlock, start, end, entityKey) => {
    var characterList = contentBlock.getCharacterList();
    while (start < end) {
      characterList = characterList.set(
        start,
        CharacterMetadata.applyEntity(characterList.get(start), entityKey)
      );
      start++;
    }
    return contentBlock.set("characterList", characterList);
  };

  //Returns all entity keys from provided fagment
  entitiesInFragment = (fragment) => {
    let entities = [];
    fragment.forEach((block) => {
      block.getCharacterList().forEach((character) => {
        const key = character.getEntity();
        if (key !== null) {
          entities.push(key);
        }
      });
    });

    return entities;
  };

  //Return entity's selection range by entityKey
  entityGetRange = (entityKey) => {
    let entityRange = {};
    if (entityKey) {
      let content = this.state.editorState.getCurrentContent();
      content.getBlocksAsArray().forEach((block) => {
        block.findEntityRanges(
          (value) => value.get("entity") === entityKey,
          (start, end) => {
            entityRange = {
              start,
              end,
              block: block.getKey(),
            };
          }
        );
      });
    }
    return entityRange;
  };

  //Return all the entities in content
  entityGetAll = (entityType = null) => {
    const content = this.state.editorState.getCurrentContent();
    const entities = [];
    content.getBlocksAsArray().forEach((block) => {
      let selectedEntity = null;
      block.findEntityRanges(
        (character) => {
          if (character.getEntity() !== null) {
            const entity = content.getEntity(character.getEntity());
            if (
              !entityType ||
              (entityType && entity.getType() === entityType)
            ) {
              selectedEntity = {
                entityKey: character.getEntity(),
                block: block.getKey(),
                entity: content.getEntity(character.getEntity()),
              };
              return true;
            }
          }
          return false;
        },
        (start, end) => {
          entities.push({ ...selectedEntity, start, end });
        }
      );
    });
    return entities;
  };

  //Fixes entity text in the USER_INPUT entities after paste
  entityFixText = () => {
    const { editorState } = this.state;
    let content = editorState.getCurrentContent();
    let lastSelection = editorState.getSelection();
    let entities = this.entityGetAll("USER_INPUT");
    entities.forEach((entity) => {
      let selection = SelectionState.createEmpty(entity.block);
      selection = selection.merge({
        anchorKey: entity.block,
        focusKey: entity.block,
        anchorOffset: entity.start,
        focusOffset: entity.end,
      });
      content = Modifier.replaceText(
        content,
        selection,
        " ",
        null,
        entity.entityKey
      );
    });
    this.onChange(
      EditorState.set(editorState, {
        currentContent: content,
        selection: lastSelection,
      })
    );
  };

  /* -----  Editor Click Handle ------ */

  editorDblClick = (e) => {
    let entityFound = false;
    if (e.target) {
      let entityKey = e.target.dataset.entity || null;
      if (entityKey) {
        entityFound = true;
        this.editorEntityDblClick(entityKey, e);
      } else {
        if (e.target.dataset.text === "true") {
          entityKey = e.target.parentElement.parentElement.dataset.entity;
          if (entityKey) {
            entityFound = true;
            this.editorEntityDblClick(entityKey, e);
          }
        }
      }
    }
  };

  editorEntityDblClick = (entityKey, e) => {
    this.setState({
      selectedEntity: entityKey,
      selectedEntityElem: e.target,
      showEntityDialog: true,
    });
  };

  editorEntityClick = (entityKey) => {
    this.highlightEntity(entityKey);
    let entityRange = this.entityGetRange(entityKey);
    if (entityRange.block) {
      let selection = SelectionState.createEmpty(entityRange.block);
      selection = selection.merge({
        anchorKey: entityRange.block,
        focusKey: entityRange.block,
        anchorOffset: entityRange.start,
        focusOffset: entityRange.end,
      });

      this.onChange(
        EditorState.forceSelection(this.state.editorState, selection)
      );
    }
  };

  editorClick = (e) => {
    const { key, type } = this.state.currentBlock;

    //Table cells selection handle
    let selectedCells = this.state.selectedCells;
    if (type !== "cell" && type !== "table") {
      selectedCells = [];
    } else {
      if (e.ctrlKey) {
        selectedCells.push(key);
      } else {
        selectedCells = [key];
      }
    }
    this.setState({ selectedCells }, () => this.highlightCells());

    if (e.target) {
      let entityKey = e.target.dataset.entity || null;

      if (entityKey) {
        this.editorEntityClick(entityKey);
      } else {
        if (this.state.editorFocused) this.highlightEntity(null);
      }

      if (this.editorComponentRef.current)
        this.editorComponentRef.current.focus();
    }
  };

  /* -------  Dialogs Handling ------ */

  onEntityDialogHide = () => {
    this.setState({ showEntityDialog: false });
    this.editorClick({ target: this.state.selectedEntityElem });
  };

  onEntityDialogSave = (entityKey, entityData) => {
    let { editorState } = this.state;
    let content = editorState.getCurrentContent();
    content.mergeEntityData(entityKey, entityData);
    this.onChange(EditorState.set(editorState, { currentContent: content }));
    this.onEntityDialogHide();
  };

  onTableDialogShow = () => {
    let table = this.tableGetForBlock(this.state.currentBlock);
    if (table && Array.isArray(table)) {
      this.setState({ showTableDialog: true, currentTable: table });
    }
  };

  onTableDialogHide = () => {
    const offset = window.pageYOffset;
    this.blockMergeData(this.state.currentBlock.key, {});
    this.setState({ showTableDialog: false, currentTable: false }, () =>
      window.scrollTo(0, offset)
    );
  };

  onTableDialogSave = (resut) => {
    const offset = window.pageYOffset;
    let table = resut.table;
    this.state.selectedCells.forEach((key) => {
      const { row, col } = tableStructure.cellLocate(table, key);
      table[row][col] = Object.assign(table[row][col], resut.data);
    });

    this.tableSave(table);
    this.setState({ showTableDialog: false, currentTable: false }, () =>
      window.scrollTo(0, offset)
    );
  };

  onPageDialogHide = () => {
    const offset = window.pageYOffset;
    this.setState({ showPageDialog: false }, () => window.scrollTo(0, offset));
  };

  onPageDialogSave = (pageSettings) => {
    const offset = window.pageYOffset;
    this.setState({ pageSettings, showPageDialog: false }, () => {
      this.applyPageSettings();
      window.scrollTo(0, offset);
    });
  };

  onParagraphDialogHide = () => {
    const offset = window.pageYOffset;
    this.blockMergeData(this.state.currentBlock.key, {});
    this.setState({ showParagraphDialog: false }, () => {
      window.scrollTo(0, offset);
    });
  };

  onParagraphDialogSave = (blockData) => {
    const { selectedBlocks, selectedCells } = this.state;
    let selectedKeys = [];
    const offset = window.pageYOffset;

    this.blocksFewMergeData(
      [...new Set(selectedKeys.concat(selectedBlocks, selectedCells))],
      blockData,
      this.state.editorState.getSelection()
    );
    this.setState({ showParagraphDialog: false }, () =>
      window.scrollTo(0, offset)
    );
  };

  // ------ Blocks ------ //
  blockStyleFn = (contentBlock) => {
    const type = contentBlock.getType();
    const data = contentBlock.getData().toJS() || {};

    let classes = ["editor-block", `editor-block-${type}`];

    if (data.align && type !== "table" && type !== "cell") {
      classes.push(`editor-block-align-${data.align}`);
    }

    for (let k in data) {
      if (type !== "table") {
        classes.push(`editor-block-data-${k}-${data[k]}`);
      }
    }

    return classes.join(" ");
  };
  blockRenderFn = (contentBlock) => {
    const type = contentBlock.getType();
    if (type === "grid") {
      let { height } = blockTableTools.blockFindInTables(
        contentBlock.getKey(),
        this.state.blockTables
      );
      return {
        component: EditorGridCell,
        editable: true,
        props: {
          onResize: this.blockOnResize,
          onContentResize: this.blockOnContentResize,
          forceHeight: height || null,
          editorWidth: this.state.editorWidth,
          columnWidth: this.state.columnWidth,
          defaultHeight: 48,
        },
      };
    }

    if (type === "table") {
      return {
        component: TableBlock,
        editable: true,
        props: {},
      };
    }
    if (type === "cell") {
      return {
        component: TableCell,
        editable: true,
        props: {},
      };
    }
  };
  blockIndent = () => {
    const data = this.state.currentBlock.data || {};
    const initialIndent = parseInt(data.paddingLeft) || 0;
    if (initialIndent >= 15) return;
    this.blockIndentChange(initialIndent + 1);
  };
  blockOutdent = () => {
    const data = this.state.currentBlock.data || {};
    const initialIndent = parseInt(data.paddingLeft) || 0;
    if (initialIndent < 1) return;
    this.blockIndentChange(initialIndent - 1);
  };
  blockIndentChange = (paddingLeft) => {
    let editorState = this.state.editorState;
    const newContentState = Modifier.mergeBlockData(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      { paddingLeft }
    );
    editorState = EditorState.push(
      editorState,
      newContentState,
      "change-block-data"
    );
    if (editorState !== this.state.editorState) this.onChange(editorState);
  };

  blocksFewMergeData = (blockKeys = [], data = {}, prevSelection = false) => {
    let editorState = this.state.editorState;
    let contentState = editorState.getCurrentContent();
    blockKeys.forEach((blockKey) => {
      contentState = Modifier.mergeBlockData(
        contentState,
        SelectionState.createEmpty(blockKey),
        data
      );
    });
    editorState = EditorState.push(editorState, contentState, "block-data");
    if (prevSelection) {
      editorState = EditorState.forceSelection(editorState, prevSelection);
    }

    this.onChange(editorState);
  };

  blockMergeData = (blockKey = false, data = {}) => {
    const currentSelection = this.state.editorState.getSelection();
    const blockSelection = blockKey
      ? SelectionState.createEmpty(blockKey)
      : currentSelection;
    this.onChange(
      EditorState.push(
        this.state.editorState,
        Modifier.mergeBlockData(
          this.state.editorState.getCurrentContent(),
          blockSelection,
          data
        ),
        "block-data"
      ),
      () => {
        this.onChange(
          EditorState.forceSelection(this.state.editorState, currentSelection)
        );
      }
    );
  };
  blockOnResize = (blockKey, w, h) => {
    this.blockMergeData(blockKey, { width: w, height: h });
  };
  blockOnContentResize = (blockKey, w, h, heightAdjust) => {
    let newData = { contentWidth: w, contentHeight: h };
    if (heightAdjust) newData.height = heightAdjust;
    this.blockMergeData(blockKey, newData);
  };
  blockForceFocus = (blockKey) => {
    if (!blockKey) return;
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();

    const block = contentState.getBlockForKey(blockKey);
    if (block) {
      const type = block.getType();
      if (type === "table" || type === "cell") {
        this.setState({ selectedCells: [blockKey] }, () =>
          this.highlightCells()
        );
      }
    }

    const newSelection = SelectionState.createEmpty(blockKey);
    this.onChange(EditorState.forceSelection(editorState, newSelection));
  };
  blockCreateBlank = () => {
    const newBlockKey = genKey();
    return new ContentBlock({
      key: newBlockKey,
      type: "unstyled",
      text: "",
      characterList: List(),
    });
  };
  blockInsertNew = (blockData = {}, afterKey = false, callbackFn = false) => {
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();
    const blockMap = contentState.getBlockMap();
    const newBlockKey = genKey();
    const newBlock = new ContentBlock({
      key: newBlockKey,
      ...blockData,
    });

    let newBlockMap = null;

    if (afterKey) {
      newBlockMap = OrderedMap().withMutations((map) => {
        for (let [k, v] of blockMap.entries()) {
          if (afterKey === k) {
            map.set(newBlock.key, newBlock);
          }
          map.set(k, v);
        }
      });
    } else {
      newBlockMap = blockMap
        .toSeq()
        .concat([[newBlock.getKey(), newBlock]])
        .toOrderedMap();
    }

    const newContent = contentState.merge({
      blockMap: newBlockMap,
    });

    this.onChange(
      EditorState.push(editorState, newContent, "insert-fragment"),
      () => {
        if (callbackFn) {
          callbackFn();
        } else {
          this.blockForceFocus(newBlockKey);
        }
      }
    );
  };
  blockClearTypesInFragment = (fragment) => {
    let newFragment = blockMapBuid.createFromArray([]);
    fragment.forEach((block, blockKey) => {
      let updatedBlock = block;
      updatedBlock = updatedBlock.merge({ type: "unstyled" });
      newFragment = newFragment.set(blockKey, updatedBlock);
    });

    return newFragment;
  };
  blockDataCollect = () => {
    const blocks = this.state.editorState
      .getCurrentContent()
      .getBlockMap()
      .toJS();
    let data = {};
    for (let key in blocks) {
      data[key] = blocks[key].data;
    }
    return data;
  };

  blockDataRestore = (blocks) => {
    if (blocks) {
      let editorState = this.state.editorState;
      let contentState = editorState.getCurrentContent();
      const currentSelection = editorState.getSelection();

      for (let key in blocks) {
        const data = blocks[key] || {};
        const blockSelection = SelectionState.createEmpty(key);
        console.log(key, data, blockSelection);
        contentState = Modifier.mergeBlockData(
          contentState,
          blockSelection,
          data
        );
      }

      this.onChange(
        EditorState.push(editorState, contentState, "block-data"),
        () => {
          this.onChange(
            EditorState.forceSelection(this.state.editorState, currentSelection)
          );
        }
      );
    }
  };

  /* ------ Save, Load, Import, Export ------ */

  onOpen = async () => {
    if (this.state.editorStateChanged) {
      let result = await this.context.ask("Закрыть текущий документ?");
      if (!result.value) return false;
    }
    this.props.onOpen();
  };
  onNew = async () => {
    let result = await this.context.ask("Закрыть текущий документ?");
    if (!result.value) return false;

    if (this.props.add) {
      this.onChange(EditorState.createEmpty(decorator));
    } else {
      this.props.onNew();
    }
  };
  onSave = async () => {
    this.setState({ pending: true });
    let html = await this.htmlSave();
    await this.props.onSave(
      convertToRaw(this.state.editorState.getCurrentContent()),
      html
    );
    this.setState({ pending: false });
  };
  onPreview = async () => {
    this.setState({ pending: true });
    let html = await this.htmlSave();

    this.setState({ pending: false, preview: html });
  };
  onPreviewClose = () => {
    this.setState({ preview: false });
  };
  htmlSave = async () => {
    const html = toHTML(this.state.editorState.getCurrentContent());
    let el = document.createElement("html");
    el.innerHTML = html;

    let portals = el.querySelectorAll(".portal-source");
    for (let portal of portals) {
      let key = portal.getAttribute("data-to");
      let gate = el.querySelector(`#portal-gate-${key}`);
      if (gate) {
        gate.innerHTML = portal.innerHTML;
      }
      portal.remove();
    }
    return el.innerHTML;
  };

  /* -------   Tables Staff --------*/

  tableColResize = (step) => {
    const tableInfo = this.tableGetCurrentInfo();
    if (tableInfo) {
      const { row, col } = tableInfo.cellLocation;
      let cell = tableInfo.table[row][col];
      let cellWidth = parseInt(cell.width) || 0;
      let newWidth = cellWidth + step;
      if (newWidth < 1) newWidth = 1;
      if (newWidth > 100) newWidth = 100;

      tableInfo.table.forEach((row, rowIdx) => {
        tableInfo.table[rowIdx][col].width = newWidth;
      });

      this.tableSave(tableInfo.table);
    }
  };

  //Makes whole row selected
  tableSelectRow = () => {
    const tableInfo = this.tableGetCurrentInfo();
    let selectedCells = [];
    if (tableInfo) {
      const selectedRow = tableInfo.cellLocation.row;
      if (
        tableInfo.table[selectedRow] &&
        Array.isArray(tableInfo.table[selectedRow])
      ) {
        tableInfo.table[selectedRow].forEach((cell) => {
          selectedCells.push(cell.key);
        });
      }
    }
    this.setState({ selectedCells }, () => this.highlightCells());
  };

  //Makes whole col selected
  tableSelectCol = () => {
    const tableInfo = this.tableGetCurrentInfo();
    let selectedCells = [];
    if (tableInfo) {
      const selectedCol = tableInfo.cellLocation.col;
      tableInfo.table.forEach((row) => {
        row.forEach((cell, index) => {
          if (index === selectedCol) {
            selectedCells.push(cell.key);
          }
        });
      });
    }
    this.setState({ selectedCells }, () => this.highlightCells());
  };

  //Removes whole row from table & call rebuild
  tableRowRemove = async () => {
    const tableInfo = this.tableGetCurrentInfo();
    if (tableInfo) {
      const selectedRow = tableInfo.cellLocation.row;

      if (tableInfo.table.length === 1) {
        this.tableRemove();
        return;
      }
      if (selectedRow === 0) {
        let result = await this.context.ask(
          "Удаление первой строки приведет к удалению таблицы. Продолжить?"
        );
        if (result.value) {
          this.tableRemove();
        }
        return;
      }

      let newLocation = {
        col: tableInfo.cellLocation.col,
        row: tableInfo.cellLocation.row - 1,
      };
      newLocation.key = tableInfo.table[newLocation.row][newLocation.col].key;

      tableInfo.table.splice(selectedRow, 1);
      this.tableSave(tableInfo.table, {
        newLocation,
      });
    }
  };

  //Removes whole col from table & call rebuild
  tableColRemove = async () => {
    const tableInfo = this.tableGetCurrentInfo();
    if (tableInfo) {
      const selectedCol = tableInfo.cellLocation.col;

      if (tableInfo.table[0].length === 1) {
        this.tableRemove();
        return;
      }
      if (selectedCol === 0) {
        let result = await this.context.ask(
          "Удаление первого столбца приведет к удалению таблицы. Продолжить?"
        );
        if (result.value) {
          this.tableRemove();
        }
        return;
      }

      let newLocation = {
        col: selectedCol - 1,
        row: tableInfo.cellLocation.row,
      };
      newLocation.key = tableInfo.table[newLocation.row][newLocation.col].key;

      let newTable = [];

      tableInfo.table.forEach((row) => {
        row.splice(selectedCol, 1);
        newTable.push(row);
      });

      this.tableSave(newTable, {
        newLocation,
      });
    }
  };

  //Removes table entierly
  tableRemove = (passEditorState) => {
    const tableInfo = this.tableGetCurrentInfo();
    if (tableInfo) {
      let editorState = passEditorState || this.state.editorState;
      const contentState = editorState.getCurrentContent();
      const blockMap = contentState.getBlockMap();
      const tableKeys = tableStructure.getAllKeys(tableInfo.table);

      let beforeFocusKey = contentState.getKeyBefore(tableKeys[0]);
      let afterFocusKey = contentState.getKeyAfter(
        tableKeys[tableKeys.length - 1]
      );

      let newBlockMap = OrderedMap().withMutations((map) => {
        for (let [k, v] of blockMap.entries()) {
          if (!tableKeys.includes(k)) {
            map.set(k, v);
          }
        }
        if (!afterFocusKey && !beforeFocusKey) {
          let blankBlock = this.blockCreateBlank();
          map.set(blankBlock.key, blankBlock);
          afterFocusKey = blankBlock.key;
        }
      });

      const newContent = contentState.merge({
        blockMap: newBlockMap,
      });
      editorState = EditorState.push(
        editorState,
        newContent,
        "remove-fragment"
      );
      if (afterFocusKey || beforeFocusKey) {
        editorState = EditorState.forceSelection(
          editorState,
          SelectionState.createEmpty(afterFocusKey || beforeFocusKey)
        );
      }

      this.setState({ editorState, editorStateChanged: true });
    }
  };

  //Returtns table[] for passed block
  tableGetForBlock = (block) => {
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();
    const { type, data } = block;
    let table = false;
    if (type === "table") {
      table = data.table;
    }
    if (type === "cell") {
      const tableBlock = contentState.getBlockForKey(data.rootKey);
      if (tableBlock) {
        const tableBlockData = tableBlock.getData().toJS();
        table = tableBlockData.table;
      }
    }
    return table;
  };

  //Returns table[] & cell coords for current block
  tableGetCurrentInfo = () => {
    let table = this.tableGetForBlock(this.state.currentBlock);
    if (!table || !Array.isArray(table)) return false;
    const cellLocation = tableStructure.cellLocate(
      table,
      this.state.currentBlock.key
    );
    return { table, json: JSON.stringify(table), cellLocation };
  };

  //Changes table[] structure to change rows/cols qty
  tableRowColAdd = (direction) => {
    const tableInfo = this.tableGetCurrentInfo();

    let table = tableInfo.table;
    if (!table || !Array.isArray(table)) return false;

    //Detect cell location & action
    const cellLocation = tableInfo.cellLocation;
    if (direction === "up" && cellLocation.row === 0) {
      this.context.toast("Нельзя доавбить строку выше первой", "warning");
      return;
    }
    if (direction === "left" && cellLocation.col === 0) {
      this.context.toast("Нельзя доавбить столбец перед первым", "warning");
      return;
    }

    let newTable = table;
    if (direction === "up" || direction === "down") {
      newTable = tableStructure.rowAdd(table, cellLocation.row, direction);
    }
    if (direction === "left" || direction === "right") {
      newTable = tableStructure.colAdd(table, cellLocation.col, direction);
    }

    this.tableSave(newTable);
  };

  //Stores table[] at root cell and calls build
  tableSave = (table, saveOptions = {}) => {
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();
    const tableRootKey = table[0][0].key;
    this.tableBuild(
      table,
      EditorState.push(
        editorState,
        Modifier.mergeBlockData(
          contentState,
          SelectionState.createEmpty(tableRootKey),
          new Map({ table: table })
        ),
        "block-data"
      ),
      saveOptions
    );
  };

  //Actually builds and maintains blocks for table[]
  tableBuild = (table = [], passEditorState, saveOptions = {}) => {
    const editorState = passEditorState || this.state.editorState;
    const contentState = editorState.getCurrentContent();
    const blockMap = contentState.getBlockMap();

    let afterKey = this.state.currentBlock.key;
    const tableRootKey = table[0][0].key;

    //Build new block map
    let newBlockMap = OrderedMap().withMutations((map) => {
      for (let [k, currBlock] of blockMap.entries()) {
        map.set(k, currBlock);

        if (afterKey === k || !afterKey) {
          table.forEach((row) => {
            row.forEach((col) => {
              let colBlock = contentState.getBlockForKey(col.key);
              if (colBlock) {
                return;
              }

              let blockParams = {
                key: col.key,
                type: col.self ? "table" : "cell",
                text: col.text,
                data: new Map({
                  table: col.self ? table : null,
                  rootKey: col.self ? null : tableRootKey,
                  ...col,
                }),
              };
              let appendBlock = new ContentBlock(blockParams);
              map.set(appendBlock.key, appendBlock);
            });
          });
        }
      }
      let blankBlock = this.blockCreateBlank();
      map.set(blankBlock.key, blankBlock);
    });

    //Reorder map properly
    let newOrderedBlockMap = OrderedMap().withMutations((map) => {
      for (let [k, currBlock] of newBlockMap.entries()) {
        let data = currBlock.getData().toJS();
        if (data.rootKey !== tableRootKey) {
          map.set(k, currBlock);
        }
        if (tableRootKey === k) {
          const tableKeys = tableStructure.getAllKeys(table);
          tableKeys.forEach((key) => {
            map.set(key, newBlockMap.get(key));
          });
        }
      }
    });

    if (saveOptions.newLocation) {
      if (saveOptions.newLocation.key) {
        afterKey = saveOptions.newLocation.key;
      }
    }

    const newContent = contentState.merge({
      blockMap: newOrderedBlockMap,
    });
    const newEditorState = EditorState.push(
      editorState,
      newContent,
      "insert-fragment"
    );

    this.setState(
      { editorState: newEditorState, editorStateChanged: true },

      () => this.blockForceFocus(afterKey)
    );
  };

  //Handles Del & Backspace ops while in table
  tableDeleteHandle = () => {
    const { editorState } = this.state;
    const initialSelection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();

    const initialAnchorKey = initialSelection.getAnchorKey();
    const initialAnchorOffset = initialSelection.getAnchorOffset();
    const initialFocusKey = initialSelection.getFocusKey();
    const initialFocusOffset = initialSelection.getFocusOffset();
    const initialBackward = initialSelection.getIsBackward();

    let newContentState = contentState;

    this.state.selectedBlocks.forEach((blockKey) => {
      let block = contentState.getBlockForKey(blockKey);

      if (block) {
        let blockSelection = SelectionState.createEmpty(blockKey);
        blockSelection = blockSelection.set("focusOffset", block.getLength());

        if (!initialBackward) {
          if (blockKey === initialAnchorKey) {
            blockSelection = blockSelection.set(
              "anchorOffset",
              initialAnchorOffset
            );
          }
          if (blockKey === initialFocusKey) {
            blockSelection = blockSelection.set(
              "focusOffset",
              initialFocusOffset
            );
          }
        } else {
          if (blockKey === initialAnchorKey) {
            blockSelection = blockSelection.set("anchorOffset", 0);
            blockSelection = blockSelection.set(
              "focusOffset",
              initialAnchorOffset
            );
          }
          if (blockKey === initialFocusKey) {
            blockSelection = blockSelection.set(
              "anchorOffset",
              initialFocusOffset
            );
          }
        }

        newContentState = Modifier.replaceText(
          newContentState,
          blockSelection,
          ""
        );
      }
    });

    //Create new selection
    let resetSelection = null;
    resetSelection = SelectionState.createEmpty(initialFocusKey);
    if (initialBackward) {
      resetSelection = resetSelection.set("anchorOffset", initialFocusOffset);
      resetSelection = resetSelection.set("focusOffset", initialFocusOffset);
    }

    //Make & apply new editor state
    let newEditorState = EditorState.push(
      editorState,
      newContentState,
      "insert-fragment"
    );
    let newEditorStateWithSelection = EditorState.forceSelection(
      newEditorState,
      resetSelection
    );

    this.onChange(newEditorStateWithSelection);
  };

  render() {
    const { editorState, inlineToolbar, preview } = this.state;
    return (
      <>
        {preview && (
          <BsModal show xl onClose={this.onPreviewClose}>
            <div
              className="documentViewer fit"
              dangerouslySetInnerHTML={{
                __html: preview,
              }}
            ></div>
          </BsModal>
        )}
        {!this.props.readOnly ? (
          <ToolbarB
            editorFocused={this.state.editorFocused}
            editorState={editorState}
            onToggleInline={this.toggleInlineStyle}
            onToggleBlock={this.toggleBlockType}
            onTogglePreset={this.togglePreset}
            onToggleScale={this.onToggleScale}
            onToggleBlockData={this.onToggleBlockData}
            onCallFunction={this.onCallFunction}
            setLink={this.setLink}
            currentScale={this.state.currentScale}
            pending={this.state.pending}
            onSave={this.props.onSave ? this.onSave : null}
            onOpen={this.props.onOpen ? this.onOpen : null}
            onNew={this.props.onNew ? this.onNew : null}
            onPreview={this.onPreview}
          />
        ) : null}

        <ToolbarA
          editorFocused={this.state.editorFocused}
          editorState={editorState}
          onToggleInline={this.toggleInlineStyle}
          onToggleBlock={this.toggleBlockType}
          onToggleScale={this.onToggleScale}
          setLink={this.setLink}
          currentScale={this.state.currentScale}
          pending={this.state.pending}
          onSave={this.props.onSave ? this.onSave : null}
          onOpen={this.props.onOpen ? this.onOpen : null}
          onNew={this.props.onNew ? this.onNew : null}
          onPreview={this.onPreview}
        />

        {!this.props.readOnly ? (
          <RightToolbar
            selectedEntity={this.state.highlightedEntityKey}
            editorFocused={this.state.editorFocused}
            editorState={editorState}
            onEntityInsert={this.entityInsert}
            onEntityInsertInline={this.entityInsertInline}
            onToggleBlock={this.toggleBlockType}
          />
        ) : null}

        {this.state.showEntityDialog && !this.props.readOnly ? (
          <EntityDialog
            editorState={editorState}
            entityKey={this.state.selectedEntity}
            onHide={this.onEntityDialogHide}
            onSave={this.onEntityDialogSave}
          />
        ) : null}
        {this.state.showPageDialog ? (
          <PageDialog
            pageSettings={this.state.pageSettings}
            onHide={this.onPageDialogHide}
            onSave={this.onPageDialogSave}
          />
        ) : null}
        {this.state.showTableDialog && !this.props.readOnly ? (
          <EditorTableDialog
            block={this.state.currentBlock}
            onHide={this.onTableDialogHide}
            onSave={this.onTableDialogSave}
            currentTable={this.state.currentTable}
          />
        ) : null}
        {this.state.showParagraphDialog && !this.props.readOnly ? (
          <ParagraphDialog
            selectedBlocks={this.state.selectedBlocks}
            block={this.state.currentBlock}
            onHide={this.onParagraphDialogHide}
            onSave={this.onParagraphDialogSave}
          />
        ) : null}

        <div
          id="editor-container"
          style={{ height: this.state.currentEditorHeight || "auto" }}
        >
          <div
            id="editor"
            ref={this.editorRef}
            className={`scale-${this.state.currentScale} ${
              this.state.editorFocused ? "focus" : "blur"
            } ${
              this.state.pageSettings.orientation === "landscape"
                ? "landscape"
                : "portrait"
            }`}
            onDoubleClick={this.editorDblClick}
            onClick={this.editorClick}
          >
            <Editor
              readOnly={this.props.readOnly}
              ref={this.editorComponentRef}
              editorState={editorState}
              onChange={this.onChange}
              customStyleMap={stylemap}
              handlePastedText={this.handlePastedText}
              handleReturn={this.handleReturn}
              handleDroppedFiles={this.handleDroppedFiles}
              keyBindingFn={this.keyBindingFn}
              blockStyleFn={this.blockStyleFn}
              blockRendererFn={this.blockRenderFn}
              onBlur={this.onBlur}
              onFocus={this.onFocus}
            />
          </div>
        </div>
      </>
    );
  }
}
